import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { Check, Clock, ExternalLink, Loader2, MapPin, Users } from "lucide-react";
import Layout from "@/components/layout/Layout";
import { Crumbs } from "@/components/layout/Crumbs";
import { Fold, FoldBody } from "@/components/layout/Fold";
import { LandingHead } from "@/components/shop/GroupCard";
import api, { formatApiError, mediaUrl } from "@/lib/api";
import { StoreButton } from "@/components/Buttons";
import { SoldBy } from "@/components/SoldBy";
import { useMall } from "@/context/MallContext";
import { cardTheme, sortStoreFirst, themeOf } from "@/lib/mall";

const inputCls = "w-full border-2 border-zinc-900 bg-white px-4 py-3 text-base text-zinc-900 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-zinc-900/30";

/** The booking form — inline at the top of the page now (Phase 10), no modal. */
function RegisterForm({ cls, theme }) {
    const [form, setForm] = useState({ name: "", email: "", phone: "" });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const submit = async (e) => {
        e?.preventDefault();
        if (!form.name || !form.email) { toast.error("Name and email required"); return; }
        setBusy(true);
        try {
            await api.post(`/classes/${cls.id}/register`, form);
            setDone(true);
            toast.success("Request sent — we'll text or email you within a business day to lock in a time.");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    if (done) return (
        <div className="border-2 border-zinc-900 p-6 text-center" data-testid="class-done">
            <Check size={40} className="mx-auto" style={{ color: theme.primary }} />
            <h3 className="mt-3 font-display text-xl font-black uppercase tracking-tight">Request sent.</h3>
            <p className="mt-1 text-sm text-zinc-700">The shop confirms your date first — you do not pay now.</p>
        </div>
    );
    return (
        <form onSubmit={submit} className="border-2 border-zinc-900 bg-white p-5 sm:p-6" data-testid="class-register-form">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em]" style={{ color: theme.primary }}>Book a seat</p>
            <h3 className="mt-1 font-display text-xl font-black uppercase tracking-tight text-zinc-900">{cls.title}</h3>
            <p className="mt-1 text-sm text-zinc-600" data-testid="class-price">${cls.price} / {cls.price_unit || "person"} · {cls.duration} · up to {cls.seats} people</p>
            <div className="mt-4 space-y-3">
                <input className={inputCls} placeholder="Name *" value={form.name} onChange={set("name")} data-testid="class-name" />
                <input className={inputCls} type="email" placeholder="Email *" value={form.email} onChange={set("email")} data-testid="class-email" />
                <input className={inputCls} type="tel" placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="class-phone" />
                <StoreButton theme={theme} type="submit" className="w-full" disabled={busy} data-testid="class-submit">
                    {busy ? <Loader2 className="animate-spin" size={16} /> : "Send request"}
                </StoreButton>
                {(cls.booking_links || []).length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-1">
                        {cls.booking_links.map((b) => (
                            <a key={b.url} href={b.url} target="_blank" rel="noreferrer" data-testid={`class-book-${b.label.split(" ").pop().toLowerCase()}`}
                                className="inline-flex items-center gap-1.5 border-2 border-zinc-900 px-3 py-2 text-xs font-bold uppercase tracking-wide text-zinc-900 hover:bg-zinc-900 hover:text-white">
                                {b.label} <ExternalLink size={12} />
                            </a>
                        ))}
                    </div>
                )}
                <p className="text-center text-[11px] text-zinc-500">You do not pay now. The shop confirms your date first.</p>
            </div>
        </form>
    );
}

/** CLASSES (Phase 10): the next class and its booking form first; the studio gallery and other classes fold away below. */
export default function Classes() {
    const [rows, setRows] = useState(null);
    const { slug: activeSlug, store: activeStore, ensureStore } = useMall();
    useEffect(() => { ensureStore(); }, [ensureStore]);
    useEffect(() => { api.get("/classes").then(({ data }) => setRows(data.classes || [])).catch(() => setRows([])); }, []);

    const sorted = useMemo(() => sortStoreFirst(rows || [], activeSlug), [rows, activeSlug]);
    const cls = sorted[0] || null;
    const others = sorted.slice(1);
    const theme = themeOf(activeStore);

    return (
        <Layout title="Classes">
            <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6" data-testid="classes-page">
                <Crumbs trail={[{ label: "Classes" }]} />
                <div className="mt-4">
                    <LandingHead kicker={cls?.location ? <span className="inline-flex items-center gap-1.5"><MapPin size={12} /> {cls.location}</span> : "Classes"} testid="classes-intro"
                        title={<span data-testid="classes-title">Learn to print in a real shop.</span>}
                        line="Hands-on classes taught inside the print shop. Pick a seat, send the request, and the shop confirms your date — you pay the shop, not us." />
                </div>
                {rows === null && <div className="flex min-h-[30vh] items-center justify-center text-zinc-400"><Loader2 className="animate-spin" /></div>}
                {rows !== null && !cls && (
                    <p className="mt-8 max-w-xl text-base text-zinc-600" data-testid="classes-empty">
                        No class is posted right now. Shops post their printing classes from the back office, and students book them here.
                    </p>
                )}
                {cls && (
                    <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(340px,440px)] lg:items-start" data-testid={`class-card-${cls.id}`}>
                        <div className="border-2 border-zinc-900 bg-white">
                            {cls.image && <img src={mediaUrl(cls.image)} alt={cls.title} className="aspect-[16/8] w-full border-b-2 border-zinc-900 object-cover" />}
                            <div className="p-5 sm:p-6">
                                <h2 className="font-display text-2xl font-black uppercase leading-tight tracking-tight text-zinc-900 sm:text-3xl">{cls.title}</h2>
                                <SoldBy store={cls.store} className="mt-2" withFulfillment={false} />
                                <p className="mt-3 text-base text-zinc-700">{cls.tagline || cls.description}</p>
                                <div className="mt-4 flex flex-wrap gap-4 font-mono text-xs uppercase tracking-widest text-zinc-700">
                                    <span className="inline-flex items-center gap-1.5"><Clock size={14} style={{ color: theme.primary }} /> {cls.duration}</span>
                                    <span className="inline-flex items-center gap-1.5"><Users size={14} style={{ color: theme.primary }} /> up to {cls.seats} people</span>
                                    {(cls.stats || []).map((s) => <span key={s}>{s}</span>)}
                                </div>
                                <ul className="mt-5 space-y-2">
                                    {(cls.highlights || []).map((h) => (
                                        <li key={h} className="flex gap-2.5 text-sm text-zinc-800"><Check size={16} className="mt-0.5 shrink-0" style={{ color: theme.primary }} /> {h}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                        <RegisterForm cls={cls} theme={cardTheme(cls, activeStore)} />
                    </div>
                )}
            </section>

            {cls && (
                <Fold title="What happens in the class" kicker="The full description" testid="classes-fold-about">
                    <FoldBody><p className="max-w-3xl whitespace-pre-line text-base text-zinc-700" data-testid="class-description">{cls.description}</p></FoldBody>
                </Fold>
            )}
            {(cls?.gallery || []).length > 0 && (
                <Fold title="Inside the studio" kicker="Photos" testid="classes-fold-gallery">
                    <FoldBody>
                        <div className="grid gap-4 sm:grid-cols-3" data-testid="class-gallery">
                            {cls.gallery.map((g) => <img key={g} src={mediaUrl(g)} alt="Studio" className="aspect-[4/5] w-full border-2 border-zinc-900 object-cover" />)}
                        </div>
                    </FoldBody>
                </Fold>
            )}
            {others.length > 0 && (
                <Fold title={`More classes (${others.length})`} kicker="From other shops and teachers" testid="classes-fold-others">
                    <FoldBody>
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="mall-classes">
                            {others.map((c) => (
                                <div key={c.id} className="flex flex-col border-2 border-zinc-900 bg-white p-5" data-testid={`mall-class-${c.id}`}>
                                    <p className="font-display text-lg font-black uppercase leading-tight tracking-tight">{c.title}</p>
                                    <SoldBy store={c.store} className="mt-2" withFulfillment={false} />
                                    <p className="mt-2 text-sm text-zinc-600">{c.duration} · up to {c.seats} people</p>
                                    <p className="mt-3 font-mono text-2xl font-black text-zinc-900">${c.price}<span className="text-xs text-zinc-500"> / {c.price_unit || "person"}</span></p>
                                </div>
                            ))}
                        </div>
                    </FoldBody>
                </Fold>
            )}
            <Fold title="Know how to print? Teach a class here" kicker="Teach with us" testid="classes-fold-teach">
                <FoldBody>
                    <div className="flex flex-col items-start gap-4 border-2 border-zinc-900 p-6 sm:flex-row sm:items-center sm:justify-between">
                        <p className="max-w-xl text-sm text-zinc-700">Post your workshop as a classified ad, we review it within a day, and students message you straight from the listing.</p>
                        <a href="/supplies/sell?kind=class"><StoreButton theme={theme} data-testid="teach-cta" className="px-5 py-3 text-xs">Post your class</StoreButton></a>
                    </div>
                </FoldBody>
            </Fold>
        </Layout>
    );
}
